import { useState } from "react";
import { Check, Copy, Terminal } from "lucide-react";
import { useTranslation } from "react-i18next";

// ---------------------------------------------------------------------------
// DocumentationPage
//
// Mémo opérationnel pour le parc de téléphones VelvetFinger. Chaque section
// regroupe des commandes copiables (Terminal block). Pour ajouter une commande,
// pousser une entrée dans le `SECTIONS` ci-dessous — pas de refacto requis.
// ---------------------------------------------------------------------------

interface DocCommand {
  title: string;
  description?: string;
  command: string;
  /** Optionnel : note à afficher sous la commande (warning, contexte, etc.). */
  note?: string;
}

interface DocSection {
  id: string;
  title: string;
  intro?: string;
  commands: DocCommand[];
}

const SECTIONS: DocSection[] = [
  {
    id: "depannage",
    title: "Dépannage téléphone",
    intro:
      "Procédures de remise en marche quand un téléphone se comporte mal sans redémarrage complet.",
    commands: [
      {
        title: "Forcer le redémarrage du daemon",
        description:
          "À utiliser quand le téléphone est figé en `busy`, que les contrôles / OCR ne répondent plus, ou que l'agent WS est déconnecté côté dashboard. Le daemon est tué et respawn automatiquement (LaunchDaemon). Le tunnel SSH `localhost:2222` doit pointer sur le téléphone visé.",
        command:
          "ssh -p 2222 root@localhost 'launchctl kickstart -k system/com.louis.velvetfingerd'",
        note: "Si la commande échoue, fallback : bootout puis bootstrap (`launchctl bootout system/com.louis.velvetfingerd ; launchctl bootstrap system /var/jb/Library/LaunchDaemons/com.louis.velvetfingerd.plist`).",
      },
    ],
  },
];

export function DocumentationPage(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="p-8 max-w-4xl">
      <header className="mb-8">
        <h1 className="text-3xl font-bold">{t("documentation.title")}</h1>
        <p className="mt-1 text-ink-500 text-sm">
          {t("documentation.subtitle")}
        </p>
      </header>

      <div className="space-y-8">
        {SECTIONS.map((section) => (
          <section key={section.id} className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold">{section.title}</h2>
              {section.intro && (
                <p className="mt-1 text-sm text-ink-500">{section.intro}</p>
              )}
            </div>
            <div className="space-y-3">
              {section.commands.map((cmd, i) => (
                <CommandCard key={i} cmd={cmd} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function CommandCard({ cmd }: { cmd: DocCommand }): JSX.Element {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(cmd.command);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // navigator.clipboard peut échouer en http (pas https) ou sandboxé ;
      // on ne casse pas l'UI, l'utilisateur peut sélectionner manuellement.
    }
  }

  return (
    <article className="rounded-xl border border-ink-700 bg-ink-800/60 overflow-hidden">
      <header className="px-4 py-3 border-b border-ink-700">
        <div className="flex items-center gap-2 text-sm font-semibold text-white">
          <Terminal className="size-4 text-velvet-400" />
          {cmd.title}
        </div>
        {cmd.description && (
          <p className="mt-1 text-xs text-ink-500 leading-relaxed">
            {cmd.description}
          </p>
        )}
      </header>
      <div className="relative bg-ink-900">
        <pre className="px-4 py-3 pr-24 text-xs font-mono text-ink-200 overflow-x-auto whitespace-pre">
          {cmd.command}
        </pre>
        <button
          onClick={() => void copy()}
          className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-ink-700 hover:bg-ink-600 text-ink-200 transition-colors"
        >
          {copied ? (
            <>
              <Check className="size-3" />
              {t("documentation.copied")}
            </>
          ) : (
            <>
              <Copy className="size-3" />
              {t("documentation.copyCmd")}
            </>
          )}
        </button>
      </div>
      {cmd.note && (
        <div className="px-4 py-2 border-t border-ink-700 text-xs text-ink-500 bg-ink-800/40">
          {cmd.note}
        </div>
      )}
    </article>
  );
}
